import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import { renderOptions } from '../form/selectOptions';
import Subheading from '../font/Subheading';
import { changeWidgetFontSettings } from '../../reducers/fit.quiz.widget.reducer';
import { useAppDispatch, useAppSelector } from '../../redux/redux.hooks';
import { FontWeight } from '../../types/fitQuizButton/ButtonState';
import { P } from '../font/Parargraph';
import ColumnItem from '../layout/ColumnItem';

import Row from '../layout/Row';
import Spacer from '../layout/Spacer';
import SectionContainer from './SectionContainer';
import RadioButtonWrapper from '../radio/RadioButtonWrapper';
import RadioButton from '../radio/RadioButton';
import RadioButtonContent from '../radio/RadioButtonContent';
import { TwitterPicker } from 'react-color';
import { useClickOutside } from '../../hooks/use.click.outside';

type WidgetFontsProps = {};

const WidgetFonts: FC<WidgetFontsProps> = () => {
  const dispatch = useAppDispatch();
  const headerColorRef = useRef<HTMLDivElement>(null);
  const bodyColorRef = useRef<HTMLDivElement>(null);
  const { fontSetting } = useAppSelector((state) => state.widgetReducer.data);
  const [toggleCustomHeaderFont, setToggleCustomHeaderFont] =
    useState<boolean>(false);
  const [toggleCustomBodyFont, setToggleCustomBodyFont] =
    useState<boolean>(false);
  const [showHeaderFontColorPicker, setShowHeaderFontColorPicker] =
    useState<boolean>(false);
  const [showBodyFontColorPicker, setShowBodyFontColorPicker] =
    useState<boolean>(false);

  useClickOutside(headerColorRef, () => setShowHeaderFontColorPicker(false));
  useClickOutside(bodyColorRef, () => setShowBodyFontColorPicker(false));

  const fontWeightOptions = [
    { label: 'Regular', value: '400' },
    { label: 'Semibold', value: '600' },
    { label: 'Bold', value: '700' },
  ];

  const yesNoOptions = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];

  const handleHeaderFontWeightChanged = (value: FontWeight) => {
    dispatch(
      changeWidgetFontSettings({ ...fontSetting, headerFontWeight: value })
    );
  };
  const handleBodyFontWeightChanged = (value: FontWeight) => {
    dispatch(
      changeWidgetFontSettings({ ...fontSetting, bodyFontWeight: value })
    );
  };
  const renderSettings = () => (
    <>
      <Row>
        <ColumnItem>
          <s-heading fontSize='large'>Customize the font of the widget</s-heading>
          <Spacer height={8} />
          <P>
            Change the font type, size and color of both the headers and regular
            text on the fitquiz widget
          </P>
        </ColumnItem>
      </Row>
      <Row>
        <ColumnItem>
          <s-heading fontSize='large'>Questions text style</s-heading>
          <s-select
            label='Font weight'
            onChange={(event) => handleHeaderFontWeightChanged(event.currentTarget.value as FontWeight)}
            value={fontSetting.headerFontWeight}
          >
            {renderOptions(fontWeightOptions)}
          </s-select>
          <Spacer height={24} />
          <s-number-field
            label='Font size'
            placeholder='Font size'
            onInput={({ currentTarget: { value } }) =>
              dispatch(
                changeWidgetFontSettings({
                  ...fontSetting,
                  headerFontSize: parseInt(value),
                })
              )
            }
            value={fontSetting.headerFontSize.toString()}
            autocomplete='off'
          />
          <Spacer height={24} />
          <s-select
            label='Only capital letters'
            onChange={({ currentTarget: { value } }) =>
              dispatch(
                changeWidgetFontSettings({
                  ...fontSetting,
                  headerFontOnlyCapitals: value === 'true',
                })
              )
            }
            value={fontSetting.headerFontOnlyCapitals?.toString()}
          >
            {renderOptions(yesNoOptions)}
          </s-select>
          <Spacer height={24} />
          <s-button
            onClick={() => setToggleCustomHeaderFont(!toggleCustomHeaderFont)}
          >
            Use custom font
          </s-button>
          {toggleCustomHeaderFont && (
            <>
              <Spacer height={24} />
              <s-text-field
                label='CUSTOM FONT NAME'
                onInput={({ currentTarget: { value } }) =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      customHeaderFontName: value,
                    })
                  )
                }
                placeholder='Please enter the font name.'
                value={fontSetting.customHeaderFontName}
                autocomplete='off'
              />
              <Spacer height={24} />
              <s-text-field
                label='CUSTOM FONT URL'
                onInput={({ currentTarget: { value } }) =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      customHeaderFontUrl: value,
                    })
                  )
                }
                placeholder='Please enter the font url.'
                value={fontSetting.customHeaderFontUrl}
                autocomplete='off'
              />
            </>
          )}
          <Spacer height={24} />
          <Subheading>QUESTION FONT COLOR</Subheading>
          <Spacer height={4} />
          <Row>
            <RadioButtonWrapper>
              <RadioButton
                groupName='question-font-color'
                value='white'
                checked={
                  fontSetting.headerFontColor.toLowerCase() ===
                  '#FFFFFF'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      headerFontColor: '#FFFFFF',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent
                    customBackground='#fff'
                    style={{ borderColor: '#e7e5e5' }}
                  />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='question-font-color'
                value='black'
                checked={
                  fontSetting.headerFontColor.toLowerCase() ===
                  '#17120F'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      headerFontColor: '#17120F',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#17120F' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='question-font-color'
                value='blue'
                checked={
                  fontSetting.headerFontColor.toLowerCase() ===
                  '#5673C0'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      headerFontColor: '#5673C0',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#5673C0' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <s-button
                onClick={() =>
                  setShowHeaderFontColorPicker(!showHeaderFontColorPicker)
                }
              >
                Custom
              </s-button>
            </RadioButtonWrapper>
          </Row>
          {showHeaderFontColorPicker && (
            <>
              <Spacer height={24} />
              <Row ref={headerColorRef}>
                <TwitterPicker
                  triangle='top-right'
                  color={fontSetting.headerFontColor}
                  onChangeComplete={(value) =>
                    dispatch(
                      changeWidgetFontSettings({
                        ...fontSetting,
                        headerFontColor: value.hex,
                      })
                    )
                  }
                />
              </Row>
            </>
          )}
        </ColumnItem>
        <ColumnItem>
          <s-heading fontSize='large'>Answers text style</s-heading>
          <s-select
            label='Font weight'
            onChange={(event) => handleBodyFontWeightChanged(event.currentTarget.value as FontWeight)}
            value={fontSetting.bodyFontWeight}
          >
            {renderOptions(fontWeightOptions)}
          </s-select>
          <Spacer height={24} />
          <s-number-field
            label='Font size'
            placeholder='Font size'
            onInput={({ currentTarget: { value } }) =>
              dispatch(
                changeWidgetFontSettings({
                  ...fontSetting,
                  bodyFontSize: parseInt(value),
                })
              )
            }
            value={fontSetting.bodyFontSize.toString()}
            autocomplete='off'
          />
          <Spacer height={24} />
          <s-select
            label='Only capital letters'
            onChange={({ currentTarget: { value } }) =>
              dispatch(
                changeWidgetFontSettings({
                  ...fontSetting,
                  bodyFontOnlyCapitals: value === 'true',
                })
              )
            }
            value={fontSetting.bodyFontOnlyCapitals?.toString()}
          >
            {renderOptions(yesNoOptions)}
          </s-select>
          <Spacer height={24} />
          <s-select
            label='Has border radius'
            onChange={({ currentTarget: { value } }) =>
              dispatch(
                changeWidgetFontSettings({
                  ...fontSetting,
                  bodyBorderHasRadius: value === 'true',
                })
              )
            }
            value={fontSetting.bodyBorderHasRadius?.toString()}
          >
            {renderOptions(yesNoOptions)}
          </s-select>
          <Spacer height={24} />
          <s-button
            onClick={() => setToggleCustomBodyFont(!toggleCustomBodyFont)}
          >
            Use custom font
          </s-button>
          <Spacer height={24} />
          <Row>
            <s-number-field
              label='Vertical padding'
              onInput={({ currentTarget: { value } }) => {
                dispatch(
                  changeWidgetFontSettings({
                    ...fontSetting,
                    bodyPadding: {
                      ...fontSetting.bodyPadding,
                      vertical: +value,
                    },
                  })
                );
              }}
              value={fontSetting.bodyPadding.vertical.toString()}
              autocomplete='off'
            />
            <Spacer width={12} />
            <s-number-field
              label='Horizontal padding'
              placeholder='Font size'
              onInput={({ currentTarget: { value } }) => {
                dispatch(
                  changeWidgetFontSettings({
                    ...fontSetting,
                    bodyPadding: {
                      ...fontSetting.bodyPadding,
                      horizontal: +value,
                    },
                  })
                );
              }}
              value={fontSetting.bodyPadding.horizontal.toString()}
              autocomplete='off'
            />
          </Row>
          {toggleCustomBodyFont && (
            <>
              <Spacer height={24} />
              <s-text-field
                label='CUSTOM FONT NAME'
                onInput={({ currentTarget: { value } }) =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      customBodyFontName: value,
                    })
                  )
                }
                placeholder='Please enter the font name.'
                value={fontSetting.customBodyFontName}
                autocomplete='off'
              />
              <Spacer height={24} />
              <s-text-field
                label='CUSTOM FONT URL'
                onInput={({ currentTarget: { value } }) =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      customBodyFontUrl: value,
                    })
                  )
                }
                placeholder='Please enter the font url.'
                value={fontSetting.customBodyFontUrl}
                autocomplete='off'
              />
            </>
          )}
          <Spacer height={24} />
          <Subheading>FONT COLOR</Subheading>
          <Spacer height={4} />
          <Row>
            <RadioButtonWrapper>
              <RadioButton
                groupName='answer-font-color'
                value='white'
                checked={
                  fontSetting.bodyFontColor.toLowerCase() ===
                  '#ffffff'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      bodyFontColor: '#ffffff',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent
                    customBackground='#fff'
                    style={{ borderColor: '#e7e5e5' }}
                  />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='answer-font-color'
                value='black'
                checked={
                  fontSetting.bodyFontColor.toLowerCase() ===
                  '#000000'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      bodyFontColor: '#000000',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#000000' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='answer-font-color'
                value='blue'
                checked={
                  fontSetting.bodyFontColor.toLowerCase() ===
                  '#5673C0'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeWidgetFontSettings({
                      ...fontSetting,
                      bodyFontColor: '#5673C0',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#5673C0' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <s-button
                onClick={() =>
                  setShowBodyFontColorPicker(!showBodyFontColorPicker)
                }
              >
                Custom
              </s-button>
            </RadioButtonWrapper>
          </Row>
          {showBodyFontColorPicker && (
            <>
              <Spacer height={24} />
              <Row ref={bodyColorRef}>
                <TwitterPicker
                  triangle='top-right'
                  color={fontSetting.bodyFontColor}
                  onChangeComplete={(value) =>
                    dispatch(
                      changeWidgetFontSettings({
                        ...fontSetting,
                        bodyFontColor: value.hex,
                      })
                    )
                  }
                />
              </Row>
            </>
          )}
        </ColumnItem>
      </Row>
    </>
  );

  const renderPreview = () => {
    const headerStyle = {
      fontSize: fontSetting.headerFontSize,
      fontWeight: fontSetting.headerFontWeight,
      textTransform: fontSetting.headerFontOnlyCapitals
        ? ('uppercase' as any)
        : ('none' as any),
      color: fontSetting.headerFontColor,
      fontFamily: fontSetting.customHeaderFontUrl
        ? fontSetting.customHeaderFontUrl
        : 'Helvetica Neue',
    };
    const bodyStyle = {
      fontSize: fontSetting.bodyFontSize,
      fontWeight: fontSetting.bodyFontWeight,
      border: `1px solid #d1d1d0`,
      padding: `${fontSetting.bodyPadding.vertical}px ${fontSetting.bodyPadding.horizontal}px`,
      borderRadius: fontSetting.bodyBorderHasRadius ? `4px` : `0px`,
      textTransform: fontSetting.bodyFontOnlyCapitals
        ? ('uppercase' as any)
        : ('none' as any),
      color: fontSetting.bodyFontColor,
      fontFamily: fontSetting.customBodyFontUrl
        ? fontSetting.customBodyFontUrl
        : 'Helvetica Neue',
    };

    return (
      <FontPreviewContainer>
        <Text style={headerStyle}>What top size do you wear?</Text>
        <Spacer height={24} />
        <Text style={bodyStyle}>XL</Text>
      </FontPreviewContainer>
    );
  };
  return (
    <SectionContainer
      renderSettingsForm={renderSettings}
      renderPreview={renderPreview}
    />
  );
};

export default WidgetFonts;

const FontPreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
`;

const Text = styled.span`
  text-align: center;
`;
